import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Title, useDataProvider, useNotify } from "react-admin";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const POLL_INTERVAL_MS = 5000;

type JobState = "waiting" | "claimed" | "running" | "completed" | "failed";

interface ScanJobError {
  message?: string;
  name?: string;
  stack?: string;
  attempt?: number;
}

interface ScanJob {
  sidequestJobId: number;
  state: JobState;
  result?: {
    findings: any;
    jobId: string;
    projectName: string;
    projectUuid?: string;
    image: string;
    tracked: boolean;
  };
  errors?: ScanJobError[];
}

interface Finding {
  _id?: string;
  name: string;
  severity?: string;
  cvssScore?: number | null;
  epssScore?: number | null;
  description?: string;
  vulnerableDependencyName?: string;
  vulnerableDependencyVersion?: string;
  vulnerableDependencyFixVersions?: string[];
  fixable?: boolean;
  [key: string]: unknown;
}

interface ScanEntry {
  imageUri: string;
  phase: "launching" | "polling" | "done" | "error";
  job: ScanJob | null;
  findings: Finding[] | null;
  errorMsg: string;
}

const ACTIVE_STATES: JobState[] = ["waiting", "claimed", "running"];

const JOB_STATE_LABELS: Record<JobState, string> = {
  waiting: "Queued",
  claimed: "Starting",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: "#D32F2F",
  high: "#F57C00",
  medium: "#FBC02D",
  low: "#FFF176",
};

const SEVERITY_RANK: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
  none: 0,
};

const severityRank = (severity?: string): number =>
  SEVERITY_RANK[severity?.toLowerCase() || ""] ?? -1;

type SortColumn = "status" | "findings" | "severity" | null;
type SortDirection = "asc" | "desc";

function extractTag(imageUri: string): string {
  const idx = imageUri.lastIndexOf(":");
  if (idx === -1 || imageUri.includes("/", idx)) return "latest";
  return imageUri.slice(idx + 1);
}

const sortableHeaderSx = {
  fontWeight: 600,
  cursor: "pointer",
  userSelect: "none",
  "&:hover": { color: "primary.main" },
} as const;

const SortIndicator: React.FC<{
  active: boolean;
  direction: SortDirection;
}> = ({ active, direction }) => {
  if (active) {
    return direction === "asc" ? (
      <ArrowUpwardIcon sx={{ fontSize: "1em" }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: "1em" }} />
    );
  }
  return <UnfoldMoreIcon sx={{ fontSize: "1em", opacity: 0.5 }} />;
};

const FindingsTable: React.FC<{ findings: Finding[]; imageUri: string }> = ({
  findings,
  imageUri,
}) => {
  const tag = extractTag(imageUri);
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(
    null,
  );

  const handleSeveritySort = () => {
    setSortDirection((prev) => {
      if (prev === null) return "desc";
      if (prev === "desc") return "asc";
      return null;
    });
  };

  const sortedFindings = !sortDirection
    ? findings
    : [...findings].sort((a, b) => {
        const comparison = severityRank(a.severity) - severityRank(b.severity);
        return sortDirection === "asc" ? comparison : -comparison;
      });

  if (findings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        No findings detected.
      </Typography>
    );
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: "action.hover" }}>
          <TableCell sx={{ fontWeight: 600 }}>CVE</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Tag</TableCell>
          <TableCell sx={sortableHeaderSx} onClick={handleSeveritySort}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
              Severity
              <SortIndicator
                active={sortDirection !== null}
                direction={sortDirection ?? "asc"}
              />
            </Box>
          </TableCell>
          <TableCell sx={{ fontWeight: 600 }}>CVSS</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Dependency Name</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Dependency Version</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Fix Version</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Fixable</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedFindings.map((f, idx) => (
          <TableRow key={f._id || idx} hover>
            <TableCell sx={{ fontFamily: "monospace", fontSize: "1em" }}>
              <Tooltip title={f.description || ""} placement="right">
                <span>{f.name}</span>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ fontFamily: "monospace", fontSize: "1em" }}>
              {tag}
            </TableCell>
            <TableCell>
              <Chip
                label={f.severity?.toUpperCase() ?? "UNKNOWN"}
                size="small"
                sx={{
                  backgroundColor:
                    SEVERITY_COLORS[f.severity?.toLowerCase() || ""] || "#999",
                  color: "black",
                  fontWeight: "bold",
                  padding: "0",
                  margin: "0",
                  borderRadius: "0.85em",
                  height: "1.5em",
                  width: "7em",
                }}
              />
            </TableCell>
            <TableCell sx={{ textAlign: "center", fontSize: "1em" }}>
              {f.cvssScore != null ? (f.cvssScore as number).toFixed(1) : "N/A"}
            </TableCell>
            <TableCell sx={{ fontSize: "1em" }}>
              {f.vulnerableDependencyName || "—"}
            </TableCell>
            <TableCell sx={{ fontSize: "1em" }}>
              {f.vulnerableDependencyVersion || "—"}
            </TableCell>
            <TableCell>
              {f.vulnerableDependencyFixVersions?.length ? (
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {f.vulnerableDependencyFixVersions.map((v) => (
                    <Chip
                      key={v}
                      label={v}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: "monospace", fontSize: "1em" }}
                    />
                  ))}
                </Box>
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell>
              <Chip
                label={f.fixable ? "Yes" : "No"}
                size="small"
                color={f.fixable ? "success" : "default"}
                variant="outlined"
                sx={{ fontFamily: "monospace", fontSize: "1em" }}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const jobStateColor = (state: JobState) => {
  if (state === "completed") return "#4CAF50";
  if (state === "failed") return "#D32F2F";
  return "#1976D2";
};

const ScanEntryRow: React.FC<{
  entry: ScanEntry;
  isExpanded: boolean;
  onToggle: (imageUri: string) => void;
}> = ({ entry, isExpanded, onToggle }) => {
  const totalFindings = entry.job?.result?.findings?.length || 0;
  const theme = useTheme();

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: "pointer" }}
        onClick={() => onToggle(entry.imageUri)}
      >
        <TableCell padding="checkbox">
          {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </TableCell>
        <TableCell
          sx={{ fontFamily: "monospace", fontWeight: 500, fontSize: "1em" }}
        >
          {entry.imageUri}
        </TableCell>
        <TableCell>
          {entry.phase === "launching" && (
            <Chip
              label="Launching"
              size="small"
              sx={{
                backgroundColor: "#1976D2",
                color: "white",
                fontWeight: "bold",
              }}
            />
          )}
          {entry.job && (
            <Chip
              label={JOB_STATE_LABELS[entry.job.state] ?? entry.job.state}
              size="small"
              sx={{
                backgroundColor: jobStateColor(entry.job.state),
                color: "white",
                fontWeight: "bold",
              }}
            />
          )}
        </TableCell>
        <TableCell>
          {entry.job?.result?.tracked !== undefined && (
            <Chip
              label={entry.job.result.tracked ? "Tracked" : "Dynamic"}
              size="small"
              variant="outlined"
              sx={{ fontFamily: "monospace", fontSize: "1em" }}
            />
          )}
        </TableCell>
        <TableCell sx={{ fontSize: "1em" }}>
          {entry.phase === "done" && entry.job?.result?.findings !== null ? (
            <Typography color="text.secondary" sx={{ fontSize: "1em" }}>
              {totalFindings}
            </Typography>
          ) : (
            "—"
          )}
        </TableCell>
        <TableCell>
          {entry.phase === "done" && entry.job?.result?.findings !== null ? (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {["critical", "high", "medium", "low"].map((severity) => {
                const count =
                  entry.job?.result?.findings.filter(
                    (f: Finding) => f.severity?.toLowerCase() === severity,
                  ).length || 0;
                if (count === 0) return null;
                return (
                  <Chip
                    key={severity}
                    label={`${severity.toUpperCase()}: ${count}`}
                    size="small"
                    sx={{
                      backgroundColor: SEVERITY_COLORS[severity],
                      color: "black",
                      fontWeight: "bold",
                      height: "1.5em",
                    }}
                  />
                );
              })}
            </Box>
          ) : (
            "—"
          )}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={6} sx={{ p: 0, border: "none" }}>
            <Box
              sx={{
                p: 2,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#2a2a2a" : "grey.50",
              }}
            >
              {(entry.phase === "launching" || entry.phase === "polling") && (
                <LinearProgress sx={{ mb: 1 }} />
              )}
              {entry.phase === "error" && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {entry.errorMsg}
                </Alert>
              )}
              {entry.phase === "done" &&
                entry.job?.result?.findings !== null && (
                  <FindingsTable
                    findings={entry?.job?.result?.findings}
                    imageUri={entry.imageUri}
                  />
                )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const EndorScanPage: React.FC = () => {
  const dataProvider = useDataProvider() as any;
  const notify = useNotify();
  const navigate = useNavigate();
  const [imageUris, setImageUris] = useState<string[]>([]);
  const [draftUri, setDraftUri] = useState("");
  const [entries, setEntries] = useState<ScanEntry[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const draftInputRef = useRef<HTMLInputElement>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [preReleaseName, setPreReleaseName] = useState("");
  const [preReleaseDescription, setPreReleaseDescription] = useState("");
  const [savingPreRelease, setSavingPreRelease] = useState(false);

  const toggleRow = (imageUri: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(imageUri)) {
        next.delete(imageUri);
      } else {
        next.add(imageUri);
      }
      return next;
    });
  };

  const handleSort = (column: SortColumn) => {
    const defaultDir: SortDirection = column === "severity" ? "desc" : "asc";
    if (sortBy !== column) {
      setSortBy(column);
      setSortDirection(defaultDir);
      return;
    }
    if (sortDirection === defaultDir) {
      setSortDirection(defaultDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(null);
      setSortDirection("asc");
    }
  };

  const getStatusPriority = (entry: ScanEntry): number => {
    if (entry.phase === "done") return 0;
    if (entry.phase === "error") return 1;
    if (entry.phase === "polling" || entry.phase === "launching") return 2;
    return 3;
  };

  const getSeverityCounts = (entry: ScanEntry): number[] => {
    const levels = ["critical", "high", "medium", "low"];
    const counts = [0, 0, 0, 0];
    const findings = entry.job?.result?.findings;
    if (!findings?.length) return counts;
    for (const finding of findings) {
      const idx = levels.indexOf(finding.severity?.toLowerCase() || "");
      if (idx !== -1) counts[idx] += 1;
    }
    return counts;
  };

  const getSortedEntries = () => {
    if (!sortBy) return entries;

    const sorted = [...entries];
    if (sortBy === "status") {
      sorted.sort((a, b) => {
        const priorityA = getStatusPriority(a);
        const priorityB = getStatusPriority(b);
        const comparison = priorityA - priorityB;
        if (comparison !== 0) return comparison;

        const stateOrder: Record<JobState, number> = {
          completed: 0,
          failed: 1,
          running: 2,
          claimed: 3,
          waiting: 4,
        };
        const stateA = a.job?.state || "waiting";
        const stateB = b.job?.state || "waiting";
        return (stateOrder[stateA] || 999) - (stateOrder[stateB] || 999);
      });
    } else if (sortBy === "findings") {
      sorted.sort((a, b) => {
        const findingsA = a.job?.result?.findings?.length || 0;
        const findingsB = b.job?.result?.findings?.length || 0;
        return sortDirection === "asc"
          ? findingsA - findingsB
          : findingsB - findingsA;
      });
    } else if (sortBy === "severity") {
      sorted.sort((a, b) => {
        const countsA = getSeverityCounts(a);
        const countsB = getSeverityCounts(b);
        for (let i = 0; i < countsA.length; i++) {
          const comparison = countsA[i] - countsB[i];
          if (comparison !== 0) {
            return sortDirection === "asc" ? comparison : -comparison;
          }
        }
        return 0;
      });
    }

    return sorted;
  };

  const sortedEntries = getSortedEntries();

  const isScanning = entries.some(
    (e) => e.phase === "launching" || e.phase === "polling",
  );

  const totalFinished = entries.filter(
    (e) => e.phase === "done" || e.phase === "error",
  ).length;
  const allSettled = entries.length > 0 && entries.length === totalFinished;

  // Poll all active entries on each render cycle until everything settles.
  useEffect(() => {
    const active = entries.filter((e) => e.phase === "polling");
    if (active.length === 0) return;

    const pollId = setTimeout(async () => {
      const updated = await Promise.all(
        entries.map(async (entry): Promise<ScanEntry> => {
          if (entry.phase !== "polling" || !entry.job) return entry;

          try {
            const { data } = await dataProvider.getEndorContainerScanStatus(
              entry.job.sidequestJobId,
            );

            if (ACTIVE_STATES.includes(data.state)) {
              return { ...entry, job: data };
            }

            if (data.state === "completed") {
              return {
                ...entry,
                phase: "done",
                job: data,
              };
            }

            const rawMsg = data.errors?.[0]?.message ?? "Scan job failed";
            const errMsg =
              rawMsg.includes("Failed to pull image") ||
              rawMsg.includes("skopeo")
                ? `Invalid image — could not pull "${entry.imageUri}". Check the image URI and try again.`
                : rawMsg;
            return { ...entry, phase: "error", job: data, errorMsg: errMsg };
          } catch (e: any) {
            return {
              ...entry,
              phase: "error",
              errorMsg: e?.message || "Network error",
            };
          }
        }),
      );
      setEntries(updated);
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(pollId);
  }, [entries, dataProvider]);

  const commitDraft = () => {
    const trimmed = draftUri.trim();
    if (trimmed && !imageUris.includes(trimmed)) {
      setImageUris((prev) => [...prev, trimmed]);
    }
    setDraftUri("");
  };

  const handleLaunch = async () => {
    // Commit any in-progress draft before launching.
    const trimmedDraft = draftUri.trim();
    const uris =
      trimmedDraft && !imageUris.includes(trimmedDraft)
        ? [...imageUris, trimmedDraft]
        : imageUris;
    if (uris.length === 0) return;
    setDraftUri("");

    setExpandedRows(new Set());

    // Seed entries immediately so the UI shows each image as "launching".
    const initial: ScanEntry[] = uris.map((uri) => ({
      imageUri: uri,
      phase: "launching",
      job: null,
      findings: null,
      errorMsg: "",
    }));
    setEntries(initial);

    // Fire all launch requests in parallel.
    const launched = await Promise.all(
      initial.map(async (entry): Promise<ScanEntry> => {
        try {
          const { data } = await dataProvider.launchEndorContainerScan(
            entry.imageUri,
          );
          return { ...entry, phase: "polling", job: data };
        } catch (e: any) {
          return {
            ...entry,
            phase: "error",
            errorMsg: e?.message || "Failed to launch scan",
          };
        }
      }),
    );
    setEntries(launched);
  };

  const handleExport = () => {
    const CSV_HEADERS = [
      "Image URI",
      "Tag",
      "CVE",
      "Severity",
      "CVSS",
      "Dependency Name",
      "Dependency Version",
      "Fix Version",
      "Fixable",
      "Description",
    ];
    const escape = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;

    const rows = entries.flatMap((entry) => {
      if (entry.phase !== "done" || !entry?.job?.result?.findings?.length)
        return [];
      const tag = extractTag(entry.imageUri);
      return entry.job.result.findings.map((f: any) =>
        [
          escape(entry.imageUri),
          escape(tag),
          escape(f.name),
          escape(f.severity ?? ""),
          escape(f.cvssScore != null ? (f.cvssScore as number).toFixed(1) : ""),
          escape(f.vulnerableDependencyName ?? ""),
          escape(f.vulnerableDependencyVersion ?? ""),
          escape(f.vulnerableDependencyFixVersions?.join(", ") ?? ""),
          escape(f.fixable ? "Yes" : "No"),
          escape(f.description ?? ""),
        ].join(","),
      );
    });

    const csv = [CSV_HEADERS.map((h) => `"${h}"`).join(","), ...rows].join(
      "\n",
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `endor-scan-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSavePreRelease = async () => {
    const trimmedName = preReleaseName.trim();
    if (!trimmedName) return;

    setSavingPreRelease(true);
    try {
      const scanEntries = entries
        .filter((e) => e.phase === "done" && e.job?.result)
        .map((e) => ({
          imageUri: e.imageUri,
          projectUuid: e.job?.result?.projectUuid,
          projectName: e.job?.result?.projectName,
          findings: e.job?.result?.findings ?? [],
        }));

      const { data } = await dataProvider.savePreRelease(
        trimmedName,
        preReleaseDescription.trim(),
        scanEntries,
      );
      notify("Pre-release saved", { type: "success" });
      setSaveDialogOpen(false);
      setPreReleaseName("");
      setPreReleaseDescription("");
      navigate(`/cve/pre-releases/${data._id}/show`);
    } catch (e: any) {
      notify(e?.message || "Failed to save pre-release", { type: "error" });
    } finally {
      setSavingPreRelease(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Image Scan" />
      <Typography variant="h5" gutterBottom>
        Image Scan
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Launch a container image scan for one or more container images. Type an
        image URI and press <strong>Enter</strong> or <strong>,</strong> to add
        it.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Chip array input */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.75,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            px: 1.5,
            py: 1,
            mb: 2,
            minHeight: 48,
            cursor: "text",
            "&:focus-within": {
              borderColor: "primary.main",
              borderWidth: 2,
              px: "11px",
              py: "7px",
            },
          }}
          onClick={() => draftInputRef.current?.focus()}
        >
          {imageUris.map((uri) => (
            <Chip
              key={uri}
              label={uri}
              size="small"
              onDelete={
                isScanning
                  ? undefined
                  : () => setImageUris((prev) => prev.filter((u) => u !== uri))
              }
              sx={{ fontFamily: "monospace", maxWidth: 400 }}
            />
          ))}
          <input
            ref={draftInputRef}
            value={draftUri}
            onChange={(e) => setDraftUri(e.target.value)}
            disabled={isScanning}
            placeholder={
              imageUris.length === 0
                ? "e.g. quay.io/astronomer/astro-runtime:12.0.0"
                : "Add another image URI…"
            }
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === ",") && draftUri.trim()) {
                e.preventDefault();
                commitDraft();
              } else if (
                e.key === "Backspace" &&
                draftUri === "" &&
                imageUris.length > 0
              ) {
                setImageUris((prev) => prev.slice(0, -1));
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = e.clipboardData.getData("text");
              const parts = pasted
                .split(/[\n,]+/)
                .map((s) => s.trim())
                .filter(Boolean);
              if (parts.length <= 1) {
                setDraftUri((prev) => (prev + (parts[0] ?? "")).trim());
                return;
              }
              setImageUris((prev) => {
                const existing = new Set(prev);
                const toAdd = parts.filter((p) => !existing.has(p));
                return [...prev, ...toAdd];
              });
              setDraftUri("");
            }}
            onBlur={commitDraft}
            style={{
              border: "none",
              outline: "none",
              flex: 1,
              minWidth: 220,
              fontSize: "0.875rem",
              fontFamily: "monospace",
              background: "transparent",
              color: "inherit",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleLaunch}
            disabled={
              isScanning || (imageUris.length === 0 && !draftUri.trim())
            }
            startIcon={
              isScanning ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PlayArrowIcon />
              )
            }
            sx={{ whiteSpace: "nowrap", minWidth: 148 }}
          >
            {isScanning
              ? `Scanning [${totalFinished}/${entries.length}]`
              : `Launch Scan${
                  imageUris.length > 1 ? ` (${imageUris.length})` : ""
                }`}
          </Button>
        </Box>
      </Paper>

      {entries.length > 0 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
            {allSettled && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={() => setSaveDialogOpen(true)}
                >
                  Save as Pre-Release
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                >
                  Export All
                </Button>
              </>
            )}
          </Box>

          <Paper>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 600 }}>Image URI</TableCell>
                  <TableCell
                    sx={sortableHeaderSx}
                    onClick={() => handleSort("status")}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      Status
                      <SortIndicator
                        active={sortBy === "status"}
                        direction={sortDirection}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell
                    sx={sortableHeaderSx}
                    onClick={() => handleSort("findings")}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      Findings
                      <SortIndicator
                        active={sortBy === "findings"}
                        direction={sortDirection}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={sortableHeaderSx}
                    onClick={() => handleSort("severity")}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      Severity Breakdown
                      <SortIndicator
                        active={sortBy === "severity"}
                        direction={sortDirection}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedEntries.map((entry) => (
                  <ScanEntryRow
                    key={entry.imageUri}
                    entry={entry}
                    isExpanded={expandedRows.has(entry.imageUri)}
                    onToggle={toggleRow}
                  />
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save as Pre-Release</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1, minWidth: 360 }}>
          <Typography variant="body2" color="text.secondary">
            Save the completed scan results as a pre-release snapshot that can
            later be compared against any existing release.
          </Typography>
          <MuiTextField
            label="Name"
            value={preReleaseName}
            onChange={(e) => setPreReleaseName(e.target.value)}
            autoFocus
            fullWidth
            required
          />
          <MuiTextField
            label="Description"
            value={preReleaseDescription}
            onChange={(e) => setPreReleaseDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePreRelease}
            disabled={!preReleaseName.trim() || savingPreRelease}
          >
            {savingPreRelease ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EndorScanPage;
